import type { FC } from 'react';
import type { RateType } from '@makemydeal/dr-platform-types/typedefs/shared/types/paymentServices';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';
import * as offerReduxActionCreators from '@makemydeal/dr-dash-store/actions/offerRedux';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import { CDL_USER_INTENT } from '@makemydeal/dr-dash-store/constants';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';

import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import type { TextInputEventValue } from '@interstate/components/TextInput';
import { NumericInput } from '@makemydeal/dr-shared-components/numeric-input';

import type { IPaymentGridErrorState } from '@makemydeal/dr-dash-types';
import type { SellRatesFromTerms } from '@makemydeal/dr-offer-redux';
import {
    constants as offerReduxConstants,
    PaymentGridSource,
    resolveByColumn,
    resolveColumnVerdict
} from '@makemydeal/dr-offer-redux';
import { LEASE } from '@makemydeal/dr-platform-types';
import {
    APR,
    GENERIC_LAST_FIELD_CHANGED_ERROR,
    INPUT_INVALID_ERROR_MESSAGE,
    MONEY_FACTOR,
    MONEY_FACTOR_TYPE,
    PAYMENT_GRID_REQUIRED_INFO_MESSAGE
} from '../../constants';
import { areStringsEqual, ENTER_KEY_CODE, getNewTarget } from '../common/utils';
import { useIsAspPaymentGridRedesignActive } from './hooks/useIsAspPaymentGridRedesignActive';
import { useIsColumnCalculated } from './hooks/useIsColumnCalculated';
import RowLabelCell from './RowLabelCell';
import { GridItem } from './styled.components';
import { disabledStyle, requiredInfoStyle } from './utils';
import { PaymentGridOverrideIndicator } from './components/PaymentGridOverrideIndicator';
import { usePaymentGridOverrideIndicator } from './hooks/usePaymentGridOverrideIndicator';

const RateTypeMap = {
    [MONEY_FACTOR_TYPE]: MONEY_FACTOR,
    [APR]: APR
} as Record<RateType, string>;

/** The two units this row can label and `convertRatesByType` can convert. `RateType` also permits
 * `InterestRate`, which has no `RateTypeMap` entry and would be converted as if it were APR, so it is never
 * stored against a term — the column keeps whatever the deal-level rate type says instead. */
type StorableRateType = Extract<RateType, 'APR' | 'MoneyFactor'>;
const MAX_APR = 100;
const storableRateType = (type?: RateType): StorableRateType | undefined =>
    type === APR || type === MONEY_FACTOR_TYPE ? type : undefined;

type SellRatesProps = {
    isLoading?: boolean;
};

const SellRates: FC<SellRatesProps> = ({ isLoading }) => {
    const dispatch = useDispatch();
    const offerType = useSelector(offerReduxSelectors.getCurrentOfferType);
    const isFlexBuyPayment = useSelector(offerReduxSelectors.isFlexBuyPayment);
    const isAspPgRedesignEnabled = useIsAspPaymentGridRedesignActive();
    const isBordered = useSelector(featureToggleSelectors.isExplicitLenderP1Enabled);
    const isPgOverrideIndicatorEnabled = useSelector(featureToggleSelectors.isPgOverrideIndicatorEnabled);
    const activeColumnIndex = useSelector(offerReduxSelectors.getResolvedPaymentGridSelectedColumnIndex);
    const overrideIndicator = usePaymentGridOverrideIndicator('sellRate');
    const isLeaseRedesign = isAspPgRedesignEnabled && offerType === LEASE;
    const columnSources = useSelector(offerReduxSelectors.getPaymentGridColumnSources);
    const decidedColumns = useSelector(offerReduxSelectors.getPaymentGridDecidedColumns);
    /** A column neither engine could price: shown blank, flagged, and open to a new term or rate. */
    const isColumnUnavailable = (month: number | null, index: number) =>
        Boolean(
            isAspPgRedesignEnabled &&
                month &&
                resolveColumnVerdict(decidedColumns, columnSources, index, month) === PaymentGridSource.Unavailable
        );

    const columnIndicesAwaitingPair = useSelector(offerReduxSelectors.getPaymentGridColumnIndicesAwaitingPair, shallowEqual);
    const columnIndicesPairRejected = useSelector(offerReduxSelectors.getPaymentGridColumnIndicesPairRejected, shallowEqual);
    const columnView = useSelector(offerReduxSelectors.getPaymentGridColumnView);
    /**
     * On Lease each rate input greys out only while its own column is in flight. Kept apart from `hasError` below so a
     * sibling column loading can never suppress a validation message.
     */
    const isColumnLoading = (month: number | null, index: number) =>
        columnView.isMasked
            ? resolveByColumn(
                  columnView.requestedColumns.size > 0,
                  () => columnView.loadingColumns.has(index),
                  () => month != null && columnView.loadingTerms.has(month)
              )
            : Boolean(isLoading);
    const isColumnSettled = (month: number | null, index: number) => !isColumnLoading(month, index);
    const isCalculatedColumn = useIsColumnCalculated();
    const isColumnCalculated = (month: number | null, index: number) =>
        isCalculatedColumn(index, month, isColumnSettled(month, index));
    const isColumnAwaitingPair = (month: number | null, index: number) =>
        isLeaseRedesign && month != null && isColumnSettled(month, index) && columnIndicesAwaitingPair.includes(index);
    const isColumnPairRejected = (month: number | null, index: number) =>
        isLeaseRedesign && month != null && isColumnSettled(month, index) && columnIndicesPairRejected.includes(index);

    // An unavailable column has no rate of its own, so it is seeded with the rate the forced call was sent with.
    const dealSellRate = useSelector(offerReduxSelectors.getSellRate);
    // A rate typed before the column has a term is parked by column index; reading it back is what survives a blur.
    const pendingSellRates = useSelector(offerReduxSelectors.getPaymentGridPendingSellRates);
    const displayedSellRate = (item: { month: number | null; val: number | null }, index: number) =>
        item.val ??
        (isAspPgRedesignEnabled ? pendingSellRates[index] : undefined) ??
        // Lease seeds nothing: no call was made for the column, so the deal's rate is another column's value. `null`
        // rather than undefined so the blur comparison below has a stable "nothing is shown" value.
        (!isLeaseRedesign && isColumnUnavailable(item.month, index) ? dealSellRate : null);

    const isSellRateMissing = (item: { month: number | null; val: number | null }, index: number) =>
        isColumnAwaitingPair(item.month, index) && displayedSellRate(item, index) == null;
    const isColumnFlagged = (item: { month: number | null; val: number | null }, index: number) =>
        isLeaseRedesign
            ? isColumnPairRejected(item.month, index) || isSellRateMissing(item, index)
            : isColumnUnavailable(item.month, index);
    const isSellRateRequired = (item: { month: number | null; val: number | null }, index: number) =>
        isLeaseRedesign && isColumnFlagged(item, index) && displayedSellRate(item, index) == null;

    const sellRates: SellRatesFromTerms = useSelector(
        isFlexBuyPayment ? offerReduxSelectors.getFlexBuyPaymentGridSellRates : offerReduxSelectors.getPaymentGridSellRates
    );
    const dealXgId = useSelector(offerReduxSelectors.getDealXgId);
    const selectedTermMonths = useSelector(offerReduxSelectors.getSelectedTermMonths);
    const paymentGridErrorState: IPaymentGridErrorState = useSelector(offerReduxSelectors.getPaymentGridErrorState);
    const currentFlexBuyPayment = useSelector(offerReduxSelectors.getCurrentFlexBuyPayments);

    const initialPaymentDiscount = currentFlexBuyPayment?.initialPaymentDiscount;

    const hasColumnMessage = sellRates.some((item, index) => isColumnFlagged(item, index));

    const rateType = sellRates[0]?.type;
    const maxSellRateFor = (type?: RateType) =>
        isAspPgRedesignEnabled && type === MONEY_FACTOR_TYPE ? offerReduxConstants.MAX_MONEY_FACTOR : MAX_APR;
    const sellRateLabel = isFlexBuyPayment ? `Override ${rateType ? RateTypeMap[rateType] : ''}` : 'Sell Rate';

    const logSellRateEdit = () =>
        logNewRelicPageAction('MV:PaymentGrid - Override sell rate', {
            mvOfferType: offerType,
            mvFieldEdited: 'mv:payment:grid:override-sell-rate',
            mvDealXgId: dealXgId
        });

    const handleChange =
        (
            columnIndex: number,
            month: number | null,
            currentDisplaySellRate?: number | null,
            percent?: number | null,
            columnRateType?: StorableRateType
        ) =>
        (e: InterstateOnChangeEvent<TextInputEventValue>) => {
            const value = e.target.value === '' ? null : parseFloat(e.target.value);

            // Compared against what the field is SHOWING, not item.val: a seeded column displays a value item.val lacks,
            // so comparing the two made every blur - including clicking a grid cell - look like an edit.
            if (currentDisplaySellRate === value) return;

            if (isAspPgRedesignEnabled && value != null && value > maxSellRateFor(columnRateType ?? rateType)) {
                return;
            }

            // No term to key an override on, so the rate is parked and the term edit converts it. A cleared field parks
            // `undefined`, or the input re-reads state and repaints the deleted value.
            if (typeof month !== 'number') {
                if (isAspPgRedesignEnabled) {
                    logSellRateEdit();
                    dispatch(offerReduxActionCreators.setPaymentGridPendingSellRate(offerType, columnIndex, value ?? undefined));
                }
                return;
            }

            if (value == null) return;

            logSellRateEdit();

            const isSelectedTerm = selectedTermMonths === month && (isFlexBuyPayment ? initialPaymentDiscount === percent : true);
            if (isAspPgRedesignEnabled) {
                dispatch(
                    offerReduxActionCreators.setPaymentGridColumnOverride(offerType, columnIndex, {
                        sellRate: value,
                        ...(columnRateType && { rateType: columnRateType })
                    })
                );
            }

            dispatch(
                offerReduxActionCreators.updateSellRateOverride(value, month, {
                    isSelectedTerm,
                    userIntent: CDL_USER_INTENT.PAYMENT_GRID_SELL_RATE,
                    rateType: columnRateType,
                    ...(isAspPgRedesignEnabled && { gridColumnIndex: columnIndex })
                })
            );

            if (isPgOverrideIndicatorEnabled && isAspPgRedesignEnabled && columnIndex === activeColumnIndex) {
                overrideIndicator.recordManualOverride();
            }
        };

    const handleKeyDownEvent = (
        columnIndex: number,
        month: number | null,
        currentSellRate: number | null,
        percent: number | null,
        event: any,
        columnRateType?: StorableRateType
    ) => {
        if (event.code === ENTER_KEY_CODE) {
            const newTarget = getNewTarget(event);
            handleChange(columnIndex, month, currentSellRate, percent, columnRateType)(newTarget);
        }
    };

    return (
        <>
            <RowLabelCell
                label={sellRateLabel}
                bordered={isBordered}
                cell={GridItem}
                cellProps={{ $growToFit: hasColumnMessage }}
                unborderedMarginTop="1.6rem"
                testId="sell-rate-label"
            />
            {sellRates.map((item, index) => (
                <GridItem
                    xs={3}
                    className={' payment-grid-item'}
                    key={
                        // Remounts the input when a later quote relabels this column's rate type, so a stale "Invalid" clears.
                        isAspPgRedesignEnabled && item.type ? `${index}-${item.val}-${item.type}` : `${index}-${item.val}`
                    }
                    paddingTop="5px"
                    border={isBordered ? undefined : 'none'}
                    data-testid={`sell-rate-${index}`}
                    $isCalculated={isColumnCalculated(item.month, index)}
                    $growToFit={hasColumnMessage}
                >
                    <PaymentGridOverrideIndicator
                        field="sellRate"
                        index={index}
                        enabled={overrideIndicator.enabled}
                        show={overrideIndicator.isShown(index)}
                        message={overrideIndicator.message}
                    >
                        <NumericInput
                            className="sell-rate-input"
                            data-testid={`sell-rate-input-${item.month}`}
                            name={'sell-rate'}
                            value={`${displayedSellRate(item, index) ?? ''}`}
                            label={item.type ? RateTypeMap[item.type] : ' '}
                            allowDecimal={true}
                            // A PQ failure sets this the instant the response lands, before the fallback can retry - it would flash an
                            hasError={
                                (areStringsEqual(paymentGridErrorState.lastUpdatedInput, `input_sell_rate_${item.month}`) &&
                                    paymentGridErrorState.hasError &&
                                    !isAspPgRedesignEnabled) ||
                                isColumnFlagged(item, index)
                            }
                            maxValue={maxSellRateFor(item.type ?? rateType)}
                            errorMessage={
                                isSellRateRequired(item, index)
                                    ? PAYMENT_GRID_REQUIRED_INFO_MESSAGE
                                    : isColumnFlagged(item, index)
                                    ? GENERIC_LAST_FIELD_CHANGED_ERROR
                                    : INPUT_INVALID_ERROR_MESSAGE
                            }
                            customErrorMessages={{ exceedsMaxValueRule: INPUT_INVALID_ERROR_MESSAGE }}
                            autoInsertCommas={false}
                            decimalMaxLength={6}
                            onBlur={handleChange(
                                index,
                                item.month,
                                displayedSellRate(item, index),
                                item.percent,
                                storableRateType(item.type)
                            )}
                            onKeyDown={(e) =>
                                handleKeyDownEvent(
                                    index,
                                    item.month,
                                    displayedSellRate(item, index),
                                    item.percent || null,
                                    e,
                                    storableRateType(item.type)
                                )
                            }
                            // An empty column accepts a sell rate before its term, so either field can be filled first.
                            disabled={isAspPgRedesignEnabled ? false : !item.month}
                            sx={{
                                ...(isColumnLoading(item.month, index) && disabledStyle),
                                ...(isSellRateRequired(item, index) && requiredInfoStyle)
                            }}
                            size="sm"
                            selectOnFocusSelector={featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap}
                        />
                    </PaymentGridOverrideIndicator>
                </GridItem>
            ))}
        </>
    );
};

export default SellRates;
